// ── 로그 관리 — 동작 설명 해석 함수 ───────────────────────────────────────────
// API 경로 + 메서드 + 요청 바디를 분석하여 한국어 동작 설명을 반환
// label: 짧은 동작명,  detail: 회사명·날짜 등 핵심 정보
// GET 포함 모든 요청에 설명 부여 — 로그만 봐도 무슨 행동인지 알 수 있도록

/**
 * API 경로·메서드·바디를 분석하여 한국어 동작 설명 반환
 * @param {string} path   — API 경로 (e.g. /api/attendance/check-in)
 * @param {string} method — HTTP 메서드 (GET|POST|PUT|PATCH|DELETE)
 * @param {object} body   — request_body JSON 객체
 * @returns {{ label: string, detail: string }}
 */
export function resolveActionDescription(path, method, body) {
  const p = String(path || '');
  const m = String(method || '').toUpperCase();
  const b = body || {};

  // ── 로그인 / 로그아웃 ───────────────────────────────────────────────────
  if (/\/api\/login/.test(p))  return { label: '로그인',  detail: b.login_id || '' };
  if (/\/api\/logout/.test(p)) return { label: '로그아웃', detail: '' };

  // ── 권한 / 내 정보 ──────────────────────────────────────────────────────
  if (/\/api\/my\/permissions/.test(p))
    return { label: '권한 확인', detail: '앱 시작·페이지 전환 시 자동 실행' };
  if (/\/api\/my\/profile/.test(p))
    return { label: '내 프로필 조회', detail: '' };
  if (/\/api\/my\/logs/.test(p))
    return { label: '내 활동 로그 조회', detail: '' };
  if (/\/api\/my\/preferences/.test(p) && m === 'GET')
    return { label: '내 설정 조회', detail: '' };
  if (/\/api\/my\/preferences/.test(p) && m !== 'GET')
    return { label: '내 설정 저장', detail: '' };

  // ── 초기설정 / 일괄등록 ───────────────────────────────────────────────────
  if (/\/api\/admin\/users\/bulk$/.test(p) && m === 'POST')
    return { label: '직원 일괄등록', detail: `${Array.isArray(b.rows) ? b.rows.length : 0}명` };
  if (/\/api\/admin\/ext\/[^/]+\/users\/bulk$/.test(p) && m === 'POST')
    return { label: '외부직원 일괄등록', detail: `${Array.isArray(b.rows) ? b.rows.length : 0}명` };
  if (/\/api\/admin\/leaves\/bulk$/.test(p) && m === 'POST')
    return { label: '연차 대량 추가', detail: `${Array.isArray(b.rows) ? b.rows.length : 0}건` };
  if (/\/api\/admin\/ext\/[^/]+\/leaves\/bulk$/.test(p) && m === 'POST')
    return { label: '외부직원 연차 대량 추가', detail: `${Array.isArray(b.rows) ? b.rows.length : 0}건` };
  if (/\/api\/admin\/departments/.test(p) && m !== 'GET')
    return { label: '부서 설정 변경', detail: b.name || b.company_code || '' };

  // ── 출퇴근 ──────────────────────────────────────────────────────────────
  if (/\/api\/attendance\/check-in/.test(p))
    return { label: '출근 체크인', detail: '' };
  if (/\/api\/attendance\/check-out/.test(p))
    return { label: '퇴근 체크아웃', detail: '' };
  if (/\/api\/admin\/attendance\/\d+/.test(p) && m !== 'GET')
    return { label: '출퇴근 기록 수정', detail: '' };
  if (/\/api\/admin\/attendance/.test(p) && m === 'GET')
    return { label: '출퇴근 관리 목록 조회', detail: '출퇴근 관리 페이지 로드' };
  if (/\/api\/attendance/.test(p) && m === 'GET')
    return { label: '내 출퇴근 현황 조회', detail: '출퇴근 페이지 로드' };

  // ── 연차 ────────────────────────────────────────────────────────────────
  if (/\/api\/(admin\/)?leaves\/\d+/.test(p) && m === 'DELETE')
    return { label: '연차 삭제', detail: b.leave_date || '' };
  if (/\/api\/(admin\/)?leaves\/\d+/.test(p) && m === 'PATCH') {
    const s = b.status === 'approved' ? '승인' : b.status === 'rejected' ? '반려' : '처리';
    return { label: `연차 ${s}`, detail: '' };
  }
  if (/\/api\/(admin\/)?leaves\/\d+/.test(p) && m === 'PUT')
    return { label: '연차 수정', detail: b.leave_date || '' };
  if (/\/api\/(admin\/)?leaves$/.test(p) && m === 'POST')
    return { label: '연차 신청', detail: b.leave_date || '' };
  if (/\/api\/admin\/leaves/.test(p) && m === 'GET')
    return { label: '연차 관리 목록 조회', detail: '연차 관리 페이지 로드' };
  if (/\/api\/leaves/.test(p) && m === 'GET')
    return { label: '내 연차 목록 조회', detail: '연차 페이지 로드' };

  // ── 일일콜 ──────────────────────────────────────────────────────────────
  if (/\/api\/(admin\/)?tm\/calls\/\d+\/keep/.test(p))
    return { label: '킵 토글', detail: '' };
  if (/\/api\/(admin\/)?tm\/calls\/\d+\/feedback/.test(p))
    return { label: '피드백 저장', detail: b.feedback || '' };
  if (/\/api\/(admin\/)?tm\/calls\/\d+/.test(p) && m === 'DELETE')
    return { label: '콜 삭제', detail: b.company_name || '' };
  if (/\/api\/(admin\/)?tm\/calls\/\d+/.test(p) && m === 'PUT') {
    const parts = [b.company_name];
    if (b.feedback) parts.push(`→ ${b.feedback}`);
    if (b.is_contracted) parts.push('계약완료');
    return { label: '콜 수정', detail: parts.filter(Boolean).join(' · ') };
  }
  if (/\/api\/(admin\/)?tm\/calls$/.test(p) && m === 'POST')
    return { label: '콜 등록', detail: [b.company_name, b.visit_date].filter(Boolean).join(' · ') };
  if (/\/api\/(admin\/)?tm\/calls/.test(p) && m === 'GET')
    return { label: '콜 목록 조회', detail: '일일콜현황 또는 달력 데이터 로드' };

  // ── 요약 ────────────────────────────────────────────────────────────────
  if (/\/api\/tm\/summary/.test(p) && m === 'GET')
    return { label: '실적 요약 통계 조회', detail: '요약 페이지 차트 데이터 로드' };

  // ── 미팅지도 ────────────────────────────────────────────────────────────
  if (/\/api\/(admin\/)?tm\/meeting-map/.test(p) && m === 'GET')
    return { label: '미팅지도 데이터 조회', detail: '미팅지도 페이지 로드' };

  // ── TM 기타 (상품목록·마케터목록·설정) ─────────────────────────────────
  if (/\/api\/tm\/ticket-types/.test(p) && m === 'GET')
    return { label: '상품 목록 조회', detail: '콜 등록창 상품 선택 목록 로드' };
  if (/\/api\/tm\/sales-persons/.test(p) && m === 'GET')
    return { label: '마케터 목록 조회', detail: '담당 마케터 선택 목록 로드' };
  if (/\/api\/tm\/preferences/.test(p) && m === 'PUT')
    return { label: 'TM 설정 저장', detail: '' };
  if (/\/api\/tm\/preferences/.test(p) && m === 'GET')
    return { label: 'TM 설정 조회', detail: '' };

  // ── 계약 ────────────────────────────────────────────────────────────────
  if (/\/api\/(admin\/)?contracts\/\d+\/cancel/.test(p))
    return { label: '계약 취소', detail: b.company_name || '' };
  if (/\/api\/(admin\/)?contracts\/\d+\/workflow-step/.test(p))
    return { label: '업무단계 변경', detail: '' };
  if (/\/api\/(admin\/)?contracts\/\d+\/promo-link/.test(p))
    return { label: '홍보물 연결', detail: '' };
  if (/\/api\/(admin\/)?contracts\/\d+/.test(p) && m === 'DELETE')
    return { label: '계약 삭제', detail: b.company_name || '' };
  if (/\/api\/(admin\/)?contracts\/\d+/.test(p) && m === 'PUT') {
    const parts = [b.company_name, b.contract_date, b.sales_person_name ? `마케터: ${b.sales_person_name}` : ''];
    return { label: '계약 등록/수정', detail: parts.filter(Boolean).join(' · ') };
  }
  if (/\/api\/(admin\/)?contracts$/.test(p) && m === 'POST') {
    const parts = [b.company_name, b.contract_date, b.sales_person_name ? `마케터: ${b.sales_person_name}` : ''];
    return { label: '계약 직접 등록', detail: parts.filter(Boolean).join(' · ') };
  }
  if (/\/api\/(admin\/)?contracts\/search/.test(p) && m === 'GET')
    return { label: '계약 검색', detail: '콜-계약 연결용 검색' };
  if (/\/api\/(admin\/)?contracts/.test(p) && m === 'GET')
    return { label: '계약 목록 조회', detail: '계약 관리 페이지 로드' };

  // ── 방문빈도 지도 ────────────────────────────────────────────────────────
  if (/\/api\/admin\/regions\/coverage/.test(p) && m === 'GET')
    return { label: '방문빈도 지도 데이터 조회', detail: '방문빈도 페이지 로드' };
  if (/\/api\/admin\/settings\/region-color-rules/.test(p) && m === 'PUT')
    return { label: '색상 기준 설정 저장', detail: '' };
  if (/\/api\/admin\/settings\/region-color-rules/.test(p) && m === 'GET')
    return { label: '색상 기준 설정 조회', detail: '' };

  // ── 홍보물 ──────────────────────────────────────────────────────────────
  if (/\/api\/admin\/promotional\/\d+/.test(p) && m === 'DELETE')
    return { label: '홍보물 삭제', detail: b.name || '' };
  if (/\/api\/admin\/promotional\/\d+/.test(p) && m === 'PUT')
    return { label: '홍보물 수정', detail: b.name || '' };
  if (/\/api\/admin\/promotional/.test(p) && m === 'POST')
    return { label: '홍보물 등록', detail: b.name || '' };
  if (/\/api\/admin\/promotional/.test(p) && m === 'GET')
    return { label: '홍보물 목록 조회', detail: '홍보물 관리 페이지 로드' };

  // ── 직원 관리 ────────────────────────────────────────────────────────────
  if (/\/api\/admin\/users\/\d+/.test(p) && m === 'DELETE')
    return { label: '직원 삭제', detail: b.name || '' };
  if (/\/api\/admin\/users\/\d+/.test(p) && (m === 'PUT' || m === 'PATCH'))
    return { label: '직원 정보 수정', detail: b.name || '' };
  if (/\/api\/admin\/users$/.test(p) && m === 'POST')
    return { label: '직원 등록', detail: b.name || '' };
  if (/\/api\/admin\/users/.test(p) && m === 'GET')
    return { label: '직원 목록 조회', detail: '직원 관리 페이지 로드' };

  // ── 외부 직원 (레거시) ───────────────────────────────────────────────────
  if (/\/api\/admin\/external-users/.test(p) && m === 'GET')
    return { label: '외부 직원 목록 조회', detail: '외부 직원 관리 페이지 로드' };

  // ── 외부 회사 관리 ───────────────────────────────────────────────────────
  if (/\/api\/admin\/external-companies\/\d+/.test(p) && m === 'DELETE')
    return { label: '외부 회사 삭제', detail: b.name || b.code || '' };
  if (/\/api\/admin\/external-companies\/\d+/.test(p) && (m === 'PUT' || m === 'PATCH'))
    return { label: '외부 회사 수정', detail: b.name || '' };
  if (/\/api\/admin\/external-companies$/.test(p) && m === 'POST')
    return { label: '외부 회사 등록', detail: b.name || '' };
  if (/\/api\/admin\/external-companies/.test(p) && m === 'GET')
    return { label: '외부 회사 목록 조회', detail: '외부 회사 관리 페이지 로드' };
  // ── 외부 회사별 직원 관리 (/admin/ext/:code/users) ──────────────────────
  if (/\/api\/admin\/ext\/[^/]+\/users\/\d+/.test(p) && m === 'DELETE')
    return { label: '외부직원 삭제', detail: b.name || '' };
  if (/\/api\/admin\/ext\/[^/]+\/users\/\d+/.test(p) && (m === 'PUT' || m === 'PATCH'))
    return { label: '외부직원 정보 수정', detail: b.name || '' };
  if (/\/api\/admin\/ext\/[^/]+\/users$/.test(p) && m === 'POST')
    return { label: '외부직원 등록', detail: b.name || '' };
  if (/\/api\/admin\/ext\/[^/]+\/users/.test(p) && m === 'GET')
    return { label: '외부직원 목록 조회', detail: '외부직원 관리 페이지 로드' };

  // ── 외부 회사별 출퇴근 관리 ─────────────────────────────────────────────
  if (/\/api\/admin\/ext\/[^/]+\/attendance/.test(p) && m !== 'GET')
    return { label: '외부직원 출퇴근 수정', detail: '' };
  if (/\/api\/admin\/ext\/[^/]+\/attendance/.test(p) && m === 'GET')
    return { label: '외부직원 출퇴근 조회', detail: '출퇴근 관리 페이지 로드' };

  // ── 외부 회사별 연차 관리 ────────────────────────────────────────────────
  if (/\/api\/admin\/ext\/[^/]+\/leaves\/\d+/.test(p) && m === 'DELETE')
    return { label: '외부직원 연차 삭제', detail: '' };
  if (/\/api\/admin\/ext\/[^/]+\/leaves\/\d+/.test(p) && (m === 'PUT' || m === 'PATCH'))
    return { label: '외부직원 연차 수정', detail: '' };
  if (/\/api\/admin\/ext\/[^/]+\/leaves$/.test(p) && m === 'POST')
    return { label: '외부직원 연차 등록', detail: '' };
  if (/\/api\/admin\/ext\/[^/]+\/leaves/.test(p) && m === 'GET')
    return { label: '외부직원 연차 조회', detail: '연차 관리 페이지 로드' };

  // ── 외부 회사별 일일콜현황 ──────────────────────────────────────────────
  if (/\/api\/admin\/ext\/[^/]+\/tm-calls/.test(p) && m !== 'GET')
    return { label: '외부직원 일일콜현황 수정', detail: '' };
  if (/\/api\/admin\/ext\/[^/]+\/tm-calls/.test(p) && m === 'GET')
    return { label: '외부직원 일일콜현황 조회', detail: '일일콜현황 페이지 로드' };

  // ── IP 관리 ──────────────────────────────────────────────────────────────
  //   옛 /api/admin/ips + 2026-10-23 출퇴근 관리 ☰ IP 창 /api/admin/ip-scope/{hq | company/코드 | menu/번호}/ips
  if (/\/api\/admin\/(ip-scope\/[^?]*\/)?ips\/\d+/.test(p) && m === 'DELETE')
    return { label: 'IP 삭제', detail: '' };
  if (/\/api\/admin\/(ip-scope\/[^?]*\/)?ips\/\d+/.test(p) && m === 'PATCH')
    return { label: 'IP 수정', detail: '' };
  if (/\/api\/admin\/(ip-scope\/[^?]*\/)?ips/.test(p) && m === 'POST')
    return { label: 'IP 등록', detail: b.ip_address || '' };
  if (/\/api\/admin\/(ip-scope\/[^?]*\/)?ips/.test(p) && m === 'GET')
    return { label: 'IP 목록 조회', detail: /ip-scope/.test(p) ? '출퇴근 관리 IP 창' : 'IP 관리 페이지 로드' };

  // ── 상품 / 카테고리 ──────────────────────────────────────────────────────
  if (/\/api\/admin\/(ticket-types|product-categories)\/\d+/.test(p) && m === 'DELETE')
    return { label: '상품/카테고리 삭제', detail: b.name || '' };
  if (/\/api\/admin\/(ticket-types|product-categories)\/\d+/.test(p) && m === 'PUT')
    return { label: '상품/카테고리 수정', detail: b.name || '' };
  if (/\/api\/admin\/(ticket-types|product-categories)$/.test(p) && m === 'POST')
    return { label: '상품/카테고리 등록', detail: b.name || '' };
  if (/\/api\/admin\/(ticket-types|product-categories)/.test(p) && m === 'GET')
    return { label: '상품/카테고리 목록 조회', detail: '상품 설정 페이지 로드' };

  // ── 업무/기능 배정 ───────────────────────────────────────────────────────
  if (/\/api\/admin\/work-assignment/.test(p) && m !== 'GET')
    return { label: '업무/기능 배정 저장', detail: '' };
  if (/\/api\/admin\/work-assignment/.test(p) && m === 'GET')
    return { label: '업무/기능 배정 조회', detail: '업무 배정 페이지 로드' };

  // ── 영업 명단 ────────────────────────────────────────────────────────────
  if (/\/api\/admin\/sales-roster/.test(p) && m === 'GET')
    return { label: '영업 명단 조회', detail: '영업 명단 페이지 로드' };

  // ── 로그 조회 ────────────────────────────────────────────────────────────
  if (/\/api\/admin\/logs/.test(p) && m === 'GET')
    return { label: '로그 조회', detail: '로그 관리 페이지 로드 또는 검색 필터 적용' };

  // ── DB 관리 ──────────────────────────────────────────────────────────────
  if (/\/api\/admin\/db/.test(p) && m === 'GET')
    return { label: 'DB 관리 데이터 조회', detail: 'DB 관리 페이지 로드' };

  // ── 설정 ─────────────────────────────────────────────────────────────────
  if (/\/api\/admin\/settings/.test(p) && m !== 'GET')
    return { label: '관리자 설정 저장', detail: '' };
  if (/\/api\/admin\/settings/.test(p) && m === 'GET')
    return { label: '관리자 설정 조회', detail: '' };

  // ── 그 외: 경로 그대로 표시 ─────────────────────────────────────────────
  const base = p.split('?')[0];
  return { label: `${m} 요청`, detail: base };
}
